import { Box, Heading, Icon, Spinner, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { ReportTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useReportDownloadUrl } from 'packages/reports/hooks/useReportDownloadUrl';
import { FiDownload } from 'react-icons/fi';

const FundingRequestReport = ({
  report,
}: {
  report: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  };
}) => {
  const { id: reportId, reportType, s3Path, isDomainReport } = report;
  const reportName = _.last(s3Path.split('/'));
  finleyAssert(reportName, 's3 path expected to exist as a report name');
  const displayName = _.upperFirst(_.lowerCase(reportType));

  const toast = useToast();

  const { getReportDownloadUrl, error, loading } = useReportDownloadUrl({
    toast,
    reportId,
    reportName,
    isDomainReport,
  });
  if (error) {
    throw error;
  }
  return (
    <Box display="flex" alignItems="center">
      <Text mr="10px">{displayName}</Text>
      {loading ? (
        <Spinner
          thickness="1px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="sm"
        />
      ) : (
        <Icon
          cursor="pointer"
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked download for ${reportName} in funding request`,
              level: Sentry.Severity.Info,
            });
            try {
              getReportDownloadUrl();
            } catch (err: any) {
              if (!toast.isActive(errorToastId)) {
                errorToastConfig.title = `Error getting report download for ${reportName}`;
                toast(errorToastConfig);
              }
              throw err;
            }
          }}
          as={FiDownload}
          marginBottom="3px"
        />
      )}
    </Box>
  );
};

export const FundingRequestReports = ({
  reports,
}: {
  reports: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  }[];
}) => {
  return (
    <Box mb="35px">
      <Heading fontSize="2xl" paddingTop="10px" mt="35px" mb="25px">
        Download Files
      </Heading>
      {_.map(reports, (report) => (
        <FundingRequestReport
          key={`funding-request-report-${report.id}`}
          report={report}
        />
      ))}
    </Box>
  );
};
